/**
 * RepairActionChip — segmented pill picker for the per-row action in
 * the Repairs builders. Iter221.
 *
 * Three actions:
 *   • 'leave'   → grey pill. No work, no charge. Default.
 *   • 'upgrade' → amber pill. Existing flashing is reused but a Dektite/
 *                  Btray is added underneath (typical Pipe/Flue work).
 *   • 'replace' → emerald pill. Full replacement — both materials and
 *                  labour priced.
 *
 * Used inline next to the F-chip on every Repairs row. Compact (h ≈ 18px)
 * so it slots into the existing card layout.
 *
 * Props:
 *   value       — current action ('leave' | 'upgrade' | 'replace')
 *   onChange    — (next) => void
 *   testid      — data-testid prefix (suffixed with `-{action}` per button)
 *   compact     — when true, hide the labels and use single-char glyphs (saves space)
 *   disableUpgrade — hide the Upgrade option (some rows only support Leave/Replace)
 */
import React from 'react';

const ACTION_STYLES = {
  leave: {
    label:  'Leave',
    glyph:  '∅',
    bg:     'bg-white',
    text:   'text-slate-600',
    border: 'border-slate-300',
    active: 'bg-slate-700 text-white border-slate-800',
    hint:   'No work — flashing stays as-is, no charge applied.',
  },
  upgrade: {
    label:  'Upgrade',
    glyph:  '↑',
    bg:     'bg-white',
    text:   'text-amber-700',
    border: 'border-amber-300',
    active: 'bg-amber-500 text-white border-amber-600',
    hint:   'Upgrade — add a Dektite/Btray underneath the existing flashing.',
  },
  replace: {
    label:  'Replace',
    glyph:  '⟲',
    bg:     'bg-white',
    text:   'text-emerald-700',
    border: 'border-emerald-300',
    active: 'bg-emerald-600 text-white border-emerald-700',
    hint:   'Full replacement — strip + supply + install priced.',
  },
};

export default function RepairActionChip({ value = 'leave', onChange, testid, compact = false, disableUpgrade = false }) {
  const options = disableUpgrade ? ['leave', 'replace'] : ['leave', 'upgrade', 'replace'];
  return (
    <div role="group" aria-label="Repair action" className="inline-flex rounded-full overflow-hidden border" data-testid={`${testid}-group`}>
      {options.map((act, idx) => {
        const s = ACTION_STYLES[act];
        const isActive = value === act;
        const first = idx === 0;
        const last = idx === options.length - 1;
        return (
          <button
            key={act}
            type="button"
            onClick={() => onChange?.(act)}
            data-testid={`${testid}-${act}`}
            title={s.hint}
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border transition-colors ${
              isActive ? s.active : `${s.bg} ${s.text} ${s.border} hover:opacity-80`
            } ${first ? '' : '-ml-px'} ${first ? 'rounded-l-full' : ''} ${last ? 'rounded-r-full' : ''}`}
          >
            {compact ? s.glyph : s.label}
          </button>
        );
      })}
    </div>
  );
}
